import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface TestingCancelModalProps {
  open: boolean;
  /** Dismiss the flow entirely (X / backdrop / Escape). */
  onClose: () => void;
  /** Return to the previous step — the cancel-reason picker. */
  onBack: () => void;
  device: Device | null;
  /** Cancels the transfer, returning the pet to the rescue household with its collar attached. */
  onConfirm: (device: Device) => Promise<void>;
}

/**
 * Intermediate confirmation shown after the rescue picks "I was just testing"
 * in the cancel-reason modal. It spells out what confirming does (the pet comes
 * back to the rescue household with its collar still attached; nothing is sent to
 * an adopter) before the transfer is actually cancelled.
 */
export default function TestingCancelModal({ open, onClose, onBack, device, onConfirm }: TestingCancelModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Cancel test transfer</ModalTitle>
      <p className={styles.modalDescription}>
        You picked <b>&ldquo;I was just testing.&rdquo;</b> Here&rsquo;s what happens when you confirm:
      </p>
      <div className={styles.outcomeBox}>
        <div className={styles.outcomeBoxIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            The transfer is cancelled — {device.petName ?? 'the pet'} comes back to <b>your rescue household</b> with
            collar <b>{device.id}</b> still attached. Nothing is sent to an adopter.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — cancel transfer'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
